import { useEffect, useState, useContext, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { OffersContext } from "../../../context/OffersContext";
import OffersSearchInput from "../OffersSearchInput/OffersSearchInput";
import Spinner from "../../Elements/Spinner/Spinner";
import LocationListItem from "../LocationListItem/LocationListItem";
import { fetchCountryPackages, fetchRegionPackages } from "../../../services/PackagesService";
import { fetchCountryPrices } from "../../../services/CountryService";
import { fetchRegionPrices } from "../../../services/RegionService";
import { getCurrentPosition, getCountryCode } from "../../../services/GeoService";
import { AuthContext } from "../../../context/AuthContext";

import "./OffersFilter.css";

const OffersFilter = () => {
  const { token, walletCurrency, loading: authLoading } = useContext(AuthContext);
  const { setSearchParams } = useContext(OffersContext);
  const [geoReady, setGeoReady] = useState(false);
  const listRef = useRef(null);

  const currency = token ? walletCurrency : "EUR";

  const COUNTRIES_LIMIT = 300;
  const REGIONS_LIMIT = 200;

  // Global state: selected location (country / region)
  const {
    selectedLocationId,
    setSelectedLocationId,
    setSelectedLocationType,
    selectedLocationName,
    setSelectedLocationName,
  } = useContext(OffersContext);

  // Location data and geo info
  const [geoCountryCode, setGeoCountryCode] = useState(null);
  const [currentRegionIds, setCurrentRegionIds] = useState([]);

  // Local UI state
  const [mode, setMode] = useState("countries"); // "popular" | "countries" | "regions" | "global"
  const [search, setSearch] = useState("");
  const [searchParams] = useSearchParams();

  // Data and loading state
  const [countries, setCountries] = useState([]);
  const [countryPrices, setCountryPrices] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [regionPrices, setRegionPrices] = useState([]);
  const [regionSearchTerm, setRegionSearchTerm] = useState("");
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Pagination + infinite scroll
  const [countriesPage, setCountriesPage] = useState(0);
  const [regionsPage, setRegionsPage] = useState(0);
  const [countriesHasMore, setCountriesHasMore] = useState(true);
  const [regionsHasMore, setRegionsHasMore] = useState(true);
  const [isFetching, setIsFetching] = useState(false);

  const getLocationParam = () => geoCountryCode || undefined;

  // Load MORE data
  const loadMore = async () => {
    if (isFetching) return;

    const hasMore = mode === "popular" || mode === "countries" ? countriesHasMore : regionsHasMore;

    if (!hasMore) return;

    setIsFetching(true);

    try {
      const page = mode === "popular" || mode === "countries" ? countriesPage + 1 : regionsPage + 1;

      if (mode === "popular" || mode === "countries") {
        const data = await fetchCountryPackages({
          skip: page * COUNTRIES_LIMIT,
          limit: COUNTRIES_LIMIT,
          popular: mode === "popular",
          currency: currency,
        });

        setCountries((prev) => [...prev, ...data.items]);
        setCountriesHasMore(data.items.length === COUNTRIES_LIMIT);
        setCountriesPage(page);
      } else {
        const data = await fetchRegionPackages({
          skip: page * REGIONS_LIMIT,
          limit: REGIONS_LIMIT,
        });
        setRegions((prev) => [...prev, ...data.items]);
        setRegionsHasMore(data.items.length === REGIONS_LIMIT);
        setRegionsPage(page);
      }
    } catch (error) {
      console.error("Failed to load more:", error);
    } finally {
      setIsFetching(false);
    }
  };

  const handleScroll = (e) => {
    const target = e.currentTarget;
    const { scrollTop, scrollHeight, clientHeight } = target;

    const hasMore = mode === "popular" || mode === "countries" ? countriesHasMore : regionsHasMore;

    if (scrollTop + clientHeight >= scrollHeight - 5 && !isFetching && hasMore) {
      loadMore();
    }
  };

  // Pick base list depending on mode (countries / regions / global)
  // Pick base list depending on mode
  const baseList =
    mode === "popular" || mode === "countries"
      ? countryPrices
      : mode === "regions"
        ? regionPrices.filter((r) => !r.id.startsWith("GL"))
        : mode === "global"
          ? regions.filter((r) => r.id.startsWith("GL"))
          : [];

  const filtered = baseList.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()));

  useEffect(() => {
    if (!listRef.current || !selectedLocationId || filtered.length === 0) return;

    let isActive = true;

    const timeout = setTimeout(() => {
      if (!isActive) return;
      if (!listRef.current) return;

      const selectedElement = listRef.current.querySelector(`[data-country-id="${selectedLocationId}"]`);

      if (!selectedElement) return;

      const container = listRef.current;

      const containerRect = container.getBoundingClientRect();
      const elementRect = selectedElement.getBoundingClientRect();

      const scrollTop = container.scrollTop + (elementRect.top - containerRect.top) - 40;

      container.scrollTo({
        top: scrollTop,
        behavior: "smooth",
      });
    }, 500);

    return () => {
      isActive = false;
      clearTimeout(timeout);
    };
  }, [countryPrices, regionPrices, selectedLocationId, filtered.length]);

  useEffect(() => {
    const urlCountryId = searchParams.get("countryId");
    const urlRegionId = searchParams.get("regionId");

    if (!selectedLocationId) {
      if (urlCountryId) {
        setMode("countries");
        setSelectedLocationId(urlCountryId);
        setSelectedLocationType("country");
      } else if (urlRegionId) {
        if (urlRegionId.startsWith("GL")) {
          setMode("global");
          setSelectedLocationId(urlRegionId);
          setSelectedLocationType("region");
        } else {
          setMode("regions");
          setSelectedLocationId(urlRegionId);
          setSelectedLocationType("region");
        }
      }
    }
  }, [searchParams, selectedLocationId]);

  useEffect(() => {
    if (!geoReady) return;

    const initMode = async () => {
      const hasData =
        mode === "popular" || mode === "countries"
          ? countryPrices.length > 0
          : regionPrices.length > 0 || regions.length > 0;

      if (!hasData) {
        setLoading(true);
        resetData();
      } else {
        setCountriesPage(0);
        setRegionsPage(0);
        setCountriesHasMore(true);
        setRegionsHasMore(true);
        setIsFetching(false);
      }
      setError("");

      const locationParam = getLocationParam();

      try {
        if (mode === "popular" || mode === "countries") {
          const data = await fetchCountryPrices({
            skip: 0,
            limit: COUNTRIES_LIMIT,
            currency: currency,
            popular: mode === "popular",
            location: locationParam,
            ...(searchTerm && { name: searchTerm }),
          });

          setCountryPrices(data.items);
          setCountriesHasMore(data.items.length === COUNTRIES_LIMIT);
          setCountriesPage(0);

          if (selectedLocationId && !selectedLocationName) {
            const found = data.items.find((c) => c.id === selectedLocationId);
            if (found) {
              setSelectedLocationName(found.name);
            }
          }

          if (!selectedLocationId && data.items.length > 0) {
            let fallback = null;

            if (geoCountryCode) {
              fallback = data.items.find((item) => item.id === geoCountryCode);
            }

            if (!fallback) {
              fallback = data.items.find((item) => item.current_location === true);
            }

            if (!fallback) {
              fallback = data.items[0];
            }

            if (!selectedLocationId && fallback) {
              setSelectedLocationId(fallback.id);
              setSelectedLocationName(fallback.name);
              setSelectedLocationType("country");
              setSearchParams({ countryId: fallback.id });
            }
          }
        } else if (mode === "global") {
          const data = await fetchRegionPrices({
            skip: 0,
            limit: REGIONS_LIMIT,
            currency: currency,
            region_id: "GL",
            location: locationParam,
          });

          setRegions(data.items);
          setRegionsHasMore(data.items.length === REGIONS_LIMIT);
          setRegionsPage(0);

          if (!selectedLocationId && data.items.length > 0) {
            const first = data.items[0];
            setSelectedLocationId(first.id);
            setSelectedLocationName(first.name);
            setSelectedLocationType("region");
          }
        } else {
          const data = await fetchRegionPrices({
            skip: 0,
            limit: REGIONS_LIMIT,
            currency: currency,
            location: locationParam,
            ...(regionSearchTerm && { name: regionSearchTerm }),
          });

          setRegionPrices(data.items);
          setRegionsHasMore(data.items.length === REGIONS_LIMIT);
          setRegionsPage(0);

          if (!selectedLocationId && data.items.length > 0) {
            const first = data.items[0];
            setSelectedLocationId(first.id);
            setSelectedLocationName(first.name);
            setSelectedLocationType("region");
          }
        }
      } catch {
        setError("Failed to load locations");
      } finally {
        setLoading(false);
      }
    };

    initMode();
  }, [mode, searchTerm, regionSearchTerm, geoCountryCode, geoReady]);

  useEffect(() => {
    const loadGeo = async () => {
      try {
        if (!navigator.geolocation) return;

        const position = await Promise.race([
          getCurrentPosition(),
          new Promise((_, reject) => setTimeout(() => reject("timeout"), 4000)), // Dłuższy czas na spokojnie
        ]);

        if (position?.latitude) {
          const code = await getCountryCode(position.latitude, position.longitude);
          setGeoCountryCode(code);
          // console.log("🌍 Current country code:", code);
        }
      } catch {
        setGeoCountryCode(null);
      } finally {
        setGeoReady(true);
      }
    };

    loadGeo();
  }, []);

  // Change between Countries / Regions / Global tabs
  const handleModeChange = async (newMode) => {
    if (newMode === mode) return;
    resetData();
    setMode(newMode);

    const type = newMode === "countries" || newMode === "popular" ? "country" : "region";
    setSelectedLocationType(type);

    setLoading(true);
    try {
      if (type === "country") {
        const data = await fetchCountryPrices({
          skip: 0,
          limit: COUNTRIES_LIMIT,
          currency,
          popular: newMode === "popular",
        });
        setCountryPrices(data.items);

        if (data.items.length > 0) {
          const fallback =
            data.items.find((i) => i.current_location) ||
            (geoCountryCode && data.items.find((i) => i.id === geoCountryCode)) ||
            data.items[0];

          setSelectedLocationId(fallback.id);
          setSelectedLocationName(fallback.name);
          setSearchParams({ countryId: fallback.id });
        }
      } else if (newMode === "global") {
        const data = await fetchRegionPrices({
          skip: 0,
          limit: REGIONS_LIMIT,
          currency,
          region_id: "GL",
        });
        setRegions(data.items);
        setRegionsHasMore(data.items.length === REGIONS_LIMIT);
        setRegionsPage(0);

        if (data.items.length > 0) {
          const firstGlobal = data.items.find((i) => i.id.startsWith("GL")) || data.items[0];

          setSelectedLocationId(firstGlobal.id);
          setSelectedLocationName(firstGlobal.name);
          setSelectedLocationType("region");
          setSearchParams({ regionId: firstGlobal.id });
        }
      } else if (newMode === "regions") {
        const data = await fetchRegionPrices({
          skip: 0,
          limit: REGIONS_LIMIT,
          currency,
          location: geoCountryCode,
        });

        setRegionPrices(data.items);
        setRegionsHasMore(data.items.length === REGIONS_LIMIT);
        setRegionsPage(0);

        if (data.items.length > 0) {
          const current = data.items.find((i) => i.current_location === true);

          const fallback = current || data.items[0];

          setSelectedLocationId(fallback.id);
          setSelectedLocationName(fallback.name);
          setSelectedLocationType("region");
          setSearchParams({ regionId: fallback.id });
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const searchPlaceholder = mode === "countries" ? "Search country" : mode === "regions" ? "Search region" : "Search";

  const resetData = () => {
    setCountries([]);
    setRegions([]);
    setCountryPrices([]);
    setRegionPrices([]);
    setCountriesPage(0);
    setRegionsPage(0);
    setCountriesHasMore(true);
    setRegionsHasMore(true);
    setIsFetching(false);
  };

  return (
    <div className="offers-filter">
      {/* Search input with icon */}
      <OffersSearchInput
        value={mode === "countries" ? searchTerm : regionSearchTerm}
        onChange={(e) => {
          if (mode === "countries") {
            setSearchTerm(e.target.value);
          } else {
            setRegionSearchTerm(e.target.value);
          }
        }}
        placeholder={searchPlaceholder}
      />

      {/* Toggle between Countries / Regions / Global */}
      <div className="offers-filter-toggle">
        {/* <button
          type="button"
          className={`offers-toggle-btn ${mode === "popular" ? "offers-toggle-btn--active" : ""}`}
          onClick={() => handleModeChange("popular")}
        >
          Popular
        </button> */}

        <button
          type="button"
          className={"offers-toggle-btn" + (mode === "countries" ? " offers-toggle-btn--active" : "")}
          onClick={() => handleModeChange("countries")}
        >
          Countries
        </button>

        <button
          type="button"
          className={"offers-toggle-btn" + (mode === "regions" ? " offers-toggle-btn--active" : "")}
          onClick={() => handleModeChange("regions")}
        >
          Regions
        </button>

        <button
          type="button"
          className={"offers-toggle-btn" + (mode === "global" ? " offers-toggle-btn--active" : "")}
          onClick={() => handleModeChange("global")}
        >
          Global
        </button>
      </div>

      {/* Scrollable list of locations */}
      <div ref={listRef} className="offers-filter-list" onScroll={handleScroll}>
        {loading && (
          <div className="loading-wrapper">
            <Spinner size="medium" />
          </div>
        )}

        {!loading &&
          !error &&
          filtered.map((item, index) => (
            <LocationListItem
              key={item.id}
              id={item.id}
              name={item.name}
              minPricePerGb={item.min_price_per_gb}
              type={mode === "popular" || mode === "countries" ? "country" : "region"}
              isCurrentLocation={item.current_location === true}
              selected={item.id === selectedLocationId}
              data-country-id={item.id}
              isFirst={index === 0}
              isLast={index === filtered.length - 1}
              onClick={() => {
                // Select location in context
                setSelectedLocationId(item.id);
                setSelectedLocationName(item.name);

                if (mode === "countries" || mode === "popular") {
                  setSearchParams({ countryId: item.id });
                } else {
                  setSearchParams({ regionId: item.id });
                }

                // On mobile, scroll to offers list panel
                if (window.innerWidth <= 640) {
                  const el = document.querySelector(".offers-main");
                  if (el) {
                    el.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                  }
                }
              }}
            />
          ))}

        {!loading && !error && filtered.length === 0 && <p className="text-muted">No results</p>}

        {error && <p className="text-muted">{error}</p>}
      </div>
    </div>
  );
};

export default OffersFilter;
